import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, FileText, AlertCircle, ExternalLink, Play, Loader2, Ban, Undo2, Plus, ChevronLeft, ChevronRight, Trash2, Calendar, Mail } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  useAuth,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Button,
  ScrollArea,
} from '@aaas/shared';
import {
  useCVDetailQuery,
  useTriggerParseMutation,
  useAttachmentSignedUrlQuery,
  useDeleteCVMutation,
} from '../../hooks/useCVQueries';
import { useJobKanbanQuery } from '../../hooks/useJobQueries';
import { PDFViewer } from '../../components/PDFViewer';
import { NotesSection } from '../../components/NotesSection';
import { CVInfoDisplay, Chip, PipelineTimeline } from '../../components/cv-detail';
import {
  useCVNotesQuery,
  useCreateNoteMutation,
  useDeleteNoteMutation,
} from '../../hooks/useCVNoteQueries';
import jobService from '../../services/job.service';
import cvService from '../../services/cv.service';
import { useQueryClient } from '@tanstack/react-query';
import { useATSRole } from '../../hooks/useATSRole';
import { RejectReasonModal } from '../../components/RejectReasonModal';
import { AddCVToJobDialog } from '../../components/AddCVToJobDialog';
import { ManageCVPoolsDialog } from '../../components/ManageCVPoolsDialog';
import { DuplicateAlert } from '../../components/DuplicateAlert';
import { CalendarEventFormDialog } from '../../components/CalendarEventFormDialog';
import { CVMeetingsSection } from '../../components/CVMeetingsSection';
import { SendEmailDialog } from '../../components/SendEmailDialog';
import { EmailThreadPanel } from '../../components/EmailThreadPanel';
import {
  PROCESSING_STATUS_CONFIG,
  type CVProcessingStatus,
  type CV,
  type CVAttachment,
  type CVJob,
  type CVTag,
} from '../../types';

// ==================== Status Badge (authenticated only) ====================

function StatusBadge({ status }: { status: CVProcessingStatus }) {
  const config = PROCESSING_STATUS_CONFIG[status];
  const colorClasses = {
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${colorClasses[config.color]}`}
    >
      <span
        className={`w-2 h-2 rounded-full mr-2 ${
          config.color === 'gray'
            ? 'bg-gray-500'
            : config.color === 'blue'
              ? 'bg-blue-500'
              : config.color === 'green'
                ? 'bg-green-500'
                : 'bg-red-500'
        }`}
      />
      {config.label}
    </span>
  );
}

// ==================== Authenticated-only Components ====================

// Format file size helper
function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Attachments Section (authenticated only - has parse button)
interface AttachmentsSectionProps {
  attachments: CVAttachment[];
  cvId: string;
  workspaceId: string | undefined;
  idToken: string | undefined;
  isProcessing: boolean;
}

function AttachmentsSection({
  attachments,
  cvId,
  workspaceId,
  idToken,
  isProcessing,
}: AttachmentsSectionProps) {
  const { t } = useTranslation('ats');
  const [openingId, setOpeningId] = useState<string | null>(null);
  const triggerParseMutation = useTriggerParseMutation({
    cvId,
    workspaceId,
    idToken,
  });

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const handleParse = (e: React.MouseEvent, attachmentId: string) => {
    e.preventDefault();
    e.stopPropagation();
    triggerParseMutation.mutate(attachmentId);
  };

  // file_url is a relative GCS path (private bucket), not a public URL. Fetch a
  // short-lived signed URL on click and open it in a new tab so the browser can
  // view (PDF) or download (DOCX) the original file.
  const handleOpen = async (e: React.MouseEvent, attachmentId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!workspaceId || !idToken || openingId) return;
    setOpeningId(attachmentId);
    try {
      const data = await cvService.getAttachmentSignedUrl(
        cvId,
        attachmentId,
        workspaceId,
        idToken
      );
      window.open(data.signed_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to open attachment', err);
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('cv.detail.attachments')}</h3>
      <div className="space-y-2">
        {attachments.map((attachment) => (
          <div
            key={attachment.id}
            className="flex items-center gap-3 p-3 rounded-lg border border-gray-100"
          >
            <button
              type="button"
              onClick={(e) => handleOpen(e, attachment.id)}
              disabled={openingId === attachment.id}
              className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-wait"
            >
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{attachment.file_name}</p>
                <p className="text-xs text-gray-500">
                  {attachment.file_type.toUpperCase()}
                  {attachment.file_size && ` - ${formatFileSize(attachment.file_size)}`}
                </p>
              </div>
              {openingId === attachment.id ? (
                <Loader2 className="h-4 w-4 text-gray-400 shrink-0 animate-spin" />
              ) : (
                <ExternalLink className="h-4 w-4 text-gray-400 shrink-0" />
              )}
            </button>
            <button
              onClick={(e) => handleParse(e, attachment.id)}
              disabled={isProcessing || triggerParseMutation.isPending}
              className="p-2 rounded-lg bg-green-100 hover:bg-green-200 text-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('cv.detail.parseCv')}
            >
              {triggerParseMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Job Card for CV-Job relationship (authenticated only)
interface JobCardProps {
  job: CVJob;
  cvId: string;
  onReject: (jobId: string) => void;
  onUnreject: (jobId: string) => void;
  onScheduleEvent: (jobId: string) => void;
  isProcessing: boolean;
  workspaceId: string | undefined;
  idToken: string | undefined;
  canReject: boolean;
}

function JobCard({
  job,
  cvId,
  onReject,
  onUnreject,
  onScheduleEvent,
  isProcessing,
  workspaceId,
  idToken,
  canReject,
}: JobCardProps) {
  const { t } = useTranslation('ats');
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('vi-VN');
  };

  return (
    <div
      className={`border rounded-lg p-4 space-y-3 ${
        job.is_rejected ? 'border-red-300 bg-red-50/30' : 'border-gray-200'
      }`}
    >
      {/* Header: Code + Position */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <Link
            to={`/ats/jobs/${job.id}`}
            className="group inline-block"
          >
            <span className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
              {job.code}
            </span>
            <p className="text-sm text-gray-600 group-hover:text-blue-500 transition-colors">
              {job.position_name}
            </p>
          </Link>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => onScheduleEvent(job.id)}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title={t('cv.detail.scheduleEvent')}
          >
            <Calendar className="h-4 w-4" />
          </button>
          {job.is_rejected ? (
            <>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                {t('cv.detail.rejected')}
              </span>
              {canReject && (
                <button
                  onClick={() => onUnreject(job.id)}
                  disabled={isProcessing}
                  className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                  title={t('cv.detail.unreject')}
                >
                  <Undo2 className="h-4 w-4" />
                </button>
              )}
            </>
          ) : (
            canReject && (
              <button
                onClick={() => onReject(job.id)}
                disabled={isProcessing}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                title={t('cv.detail.rejectCandidate')}
              >
                <Ban className="h-4 w-4" />
              </button>
            )
          )}
        </div>
      </div>

      {/* Campaign + Pipeline */}
      <div className="text-xs text-gray-500">
        <span>{job.campaign_name}</span>
        {job.pipeline_name && <span> • {job.pipeline_name}</span>}
      </div>

      {/* Pipeline Step */}
      {job.round && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{t('cv.detail.step')}:</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
            {job.round.name}
          </span>
        </div>
      )}

      {/* Rejection Reason */}
      {job.is_rejected && (job.rejection_reason || job.rejection_reason_text) && (
        <div className="text-xs text-red-600 bg-red-50 rounded p-2">
          <span className="font-medium">{t('cv.detail.reason')}: </span>
          {job.rejection_reason?.reason || job.rejection_reason_text}
        </div>
      )}

      {/* Metadata: Source, Applied At, Onboard Date */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 pt-1 border-t border-gray-100">
        {job.source && <span>{t('cv.detail.source')}: {job.source.name}</span>}
        {job.applied_at && <span>{t('cv.detail.applied')}: {formatDate(job.applied_at)}</span>}
        {job.onboard_date && <span>{t('cv.detail.onboard')}: {formatDate(job.onboard_date)}</span>}
      </div>

      {/* Pipeline Timeline - integrated inside the card */}
      {job.pipeline_id && (
        <div className="pt-3 border-t border-gray-100">
          <PipelineTimeline
            pipelineId={job.pipeline_id}
            pipelineName={job.pipeline_name || null}
            currentRoundId={job.round?.id || null}
            jobId={job.id}
            cvId={cvId}
            isRejected={job.is_rejected}
            workspaceId={workspaceId}
            idToken={idToken}
            variant="inline"
            disabled={!canReject}
          />
        </div>
      )}
    </div>
  );
}

// CV Tags Section
function CVTagsSection({ tags }: { tags: CVTag[] }) {
  const { t } = useTranslation('ats');
  if (!tags || tags.length === 0) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('cv.detail.tags')}</h3>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
          >
            {tag.name}
          </span>
        ))}
      </div>
    </div>
  );
}

// Recruitment Info Section (authenticated only - Pools & Positions)
interface RecruitmentInfoProps {
  cv: CV;
  onAddToPool: () => void;
  onRemoveFromPool: (poolId: string) => void;
}

function RecruitmentInfo({ cv, onAddToPool, onRemoveFromPool }: RecruitmentInfoProps) {
  const { t } = useTranslation('ats');
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('cv.detail.recruitmentInfo')}</h3>

      {/* Pools */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-500">{t('cv.detail.pools')}</h4>
          <Button size="sm" variant="outline" onClick={onAddToPool} className="h-6 px-2 text-xs">
            <Plus className="h-3 w-3 mr-1" />
            {t('cv.detail.addPool')}
          </Button>
        </div>
        {cv.pools && cv.pools.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {cv.pools.map((pool) => (
              <Chip key={pool.id} label={pool.name} onRemove={() => onRemoveFromPool(pool.id)} />
            ))}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">{t('cv.detail.noPools')}</span>
        )}
      </div>

      {/* Positions */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-500 mb-2">{t('cv.detail.positions')}</h4>
        {cv.positions && cv.positions.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {cv.positions.map((position) => (
              <Chip key={position.id} label={position.name} />
            ))}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">{t('cv.detail.noPositions')}</span>
        )}
      </div>

      {/* Created Date */}
      <div className="pt-4 border-t border-gray-100">
        <span className="text-sm text-gray-500">
          {t('cv.detail.createdAt', { date: new Date(cv.created_at).toLocaleDateString() })}
        </span>
      </div>
    </div>
  );
}

// Applied Jobs Section with Pipeline Timeline for each job
interface AppliedJobsSectionProps {
  cv: CV;
  cvId: string;
  onRejectJob: (jobId: string) => void;
  onUnrejectJob: (jobId: string) => void;
  onScheduleEvent: (jobId: string) => void;
  onAddToJob: () => void;
  isProcessing: boolean;
  workspaceId: string | undefined;
  idToken: string | undefined;
  canReject: boolean;
}

function AppliedJobsSection({
  cv,
  cvId,
  onRejectJob,
  onUnrejectJob,
  onScheduleEvent,
  onAddToJob,
  isProcessing,
  workspaceId,
  idToken,
  canReject,
}: AppliedJobsSectionProps) {
  const { t } = useTranslation('ats');
  if (!cv.jobs || cv.jobs.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('cv.detail.appliedJobs')}</h3>
          {canReject && (
            <Button size="sm" onClick={onAddToJob} className="flex items-center gap-1">
              <Plus className="h-4 w-4" />
              {t('cv.detail.addToJob')}
            </Button>
          )}
        </div>
        <span className="text-gray-400 text-sm">{t('cv.detail.noJobs')}</span>
      </div>
    );
  }

  // Sort jobs to maintain consistent order (by applied_at, then by id)
  const sortedJobs = [...cv.jobs].sort((a, b) => {
    // Primary sort: by applied_at (newest first)
    if (a.applied_at && b.applied_at) {
      return new Date(b.applied_at).getTime() - new Date(a.applied_at).getTime();
    }
    if (a.applied_at) return -1;
    if (b.applied_at) return 1;
    // Fallback: sort by id for stable ordering
    return a.id.localeCompare(b.id);
  });

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="p-5 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {t('cv.detail.appliedJobsCount', { count: cv.jobs.length })}
        </h3>
        {canReject && (
          <Button size="sm" onClick={onAddToJob} className="flex items-center gap-1">
            <Plus className="h-4 w-4" />
            {t('cv.detail.addToJob')}
          </Button>
        )}
      </div>
      <ScrollArea disableTableDisplay className="h-[calc((100vh-8.6rem)/2)]">
        <div className="p-4 space-y-4">
          {sortedJobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              cvId={cvId}
              onReject={onRejectJob}
              onUnreject={onUnrejectJob}
              onScheduleEvent={onScheduleEvent}
              isProcessing={isProcessing}
              workspaceId={workspaceId}
              idToken={idToken}
              canReject={canReject}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

// ==================== Main Component ====================

export function CVDetailPage() {
  const { t } = useTranslation('ats');
  const { cvId } = useParams<{ cvId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, currentWorkspace, idToken } = useAuth();
  const queryClient = useQueryClient();

  // Get job and round context from URL params
  const jobId = searchParams.get('jobId');
  const roundId = searchParams.get('roundId');
  const hideRejectedParam = searchParams.get('hideRejected');
  const hideRejected = hideRejectedParam === null ? true : hideRejectedParam === 'true';
  const showOnlyMyPicParam = searchParams.get('showOnlyMyPic');

  // Tab state - default to "parsed" for backward compatibility
  const [activeTab, setActiveTab] = useState<string>('parsed');

  // Note form state
  const [noteContent, setNoteContent] = useState('');

  // Reject modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Merge loading state
  const [isMerging, setIsMerging] = useState(false);

  // Add to job modal state
  const [showAddToJobDialog, setShowAddToJobDialog] = useState(false);

  // Role
  const { isRecruiter, isLeaderOrAbove, currentUserAccountId } = useATSRole();

  // Add to pool modal state
  const [showAddToPoolDialog, setShowAddToPoolDialog] = useState(false);

  // Calendar event state
  const [calendarFormOpen, setCalendarFormOpen] = useState(false);
  const [calendarJobId, setCalendarJobId] = useState<string | null>(null);

  // Delete CV state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSendEmailDialog, setShowSendEmailDialog] = useState(false);

  const {
    data: cv,
    isLoading: loading,
    error,
  } = useCVDetailQuery({
    cvId,
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: !!user && !!currentWorkspace && !!cvId,
  });

  // Fetch job kanban data if we have job context (for navigation)
  const { data: jobData } = useJobKanbanQuery({
    jobId: jobId || undefined,
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: !!user && !!currentWorkspace && !!jobId && !!roundId,
  });

  // Check if current recruiter is PIC for this CV in the active job context
  const isMyPic =
    isRecruiter &&
    !!currentUserAccountId &&
    !!jobData &&
    jobData.columns
      .flatMap((col) => col.cvs)
      .find((card) => card.id === cvId)?.person_in_charge_id === currentUserAccountId;
  const canReject = isLeaderOrAbove || isMyPic;

  // Compute next/previous CV IDs in the current round
  const { previousCvId, nextCvId, currentIndex, totalInRound } = useMemo(() => {
    if (!jobData || !roundId || !cvId) {
      return { previousCvId: null, nextCvId: null, currentIndex: -1, totalInRound: 0 };
    }

    const round = jobData.columns.find((col) => col.id === roundId);
    if (!round || !round.cvs.length) {
      return { previousCvId: null, nextCvId: null, currentIndex: -1, totalInRound: 0 };
    }

    // Filter out rejected CVs if hideRejected is true
    const filteredCvs = hideRejected
      ? round.cvs.filter((cv) => !cv.is_rejected)
      : round.cvs;

    if (!filteredCvs.length) {
      return { previousCvId: null, nextCvId: null, currentIndex: -1, totalInRound: 0 };
    }

    const cvIds = filteredCvs.map((cv) => cv.id);
    const currentIndex = cvIds.findIndex((id) => id === cvId);

    if (currentIndex === -1) {
      return { previousCvId: null, nextCvId: null, currentIndex: -1, totalInRound: cvIds.length };
    }

    const previousCvId = currentIndex > 0 ? cvIds[currentIndex - 1] : null;
    const nextCvId = currentIndex < cvIds.length - 1 ? cvIds[currentIndex + 1] : null;

    return { previousCvId, nextCvId, currentIndex, totalInRound: cvIds.length };
  }, [jobData, roundId, cvId, hideRejected]);

  // Navigation handlers
  const handleNavigateCV = (targetCvId: string) => {
    if (jobId && roundId) {
      const params = new URLSearchParams({
        jobId,
        roundId,
      });
      if (hideRejectedParam !== null) {
        params.set('hideRejected', hideRejectedParam);
      }
      if (showOnlyMyPicParam !== null) {
        params.set('showOnlyMyPic', showOnlyMyPicParam);
      }
      navigate(`/ats/cvs/${targetCvId}?${params.toString()}`);
    } else {
      navigate(`/ats/cvs/${targetCvId}`);
    }
  };

  // Notes queries
  const { data: notesData, isLoading: notesLoading } = useCVNotesQuery({
    cvId: cvId || '',
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: !!cvId && !!currentWorkspace?.id && !!idToken,
  });

  const createNoteMutation = useCreateNoteMutation({
    cvId: cvId || '',
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
  });

  const deleteNoteMutation = useDeleteNoteMutation({
    cvId: cvId || '',
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
  });

  const deleteCVMutation = useDeleteCVMutation({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
  });

  // Filter viewable attachments (PDF and DOCX)
  const viewableAttachments =
    cv?.attachments?.filter((att) => {
      const type = att.file_type.toLowerCase();
      return type === 'pdf' || type === 'docx';
    }) || [];

  // Selected attachment (first one by default)
  const [selectedPdfId, setSelectedPdfId] = useState<string | undefined>(undefined);

  // Get the selected attachment ID (use first viewable if not set)
  const currentPdfAttachmentId = selectedPdfId || viewableAttachments[0]?.id;
  const currentAttachment = viewableAttachments.find((att) => att.id === currentPdfAttachmentId);
  const isDocx = currentAttachment?.file_type.toLowerCase() === 'docx';

  // Fetch signed URL only when "original" tab is active and we have a viewable attachment
  const {
    data: signedUrlData,
    isLoading: signedUrlLoading,
    error: signedUrlError,
  } = useAttachmentSignedUrlQuery({
    cvId,
    attachmentId: currentPdfAttachmentId,
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: activeTab === 'original' && !!currentPdfAttachmentId,
    preview: isDocx,
  });

  const handleClose = () => {
    // If we came from a job context, go back to that job
    if (jobId) {
      const params = new URLSearchParams();
      if (hideRejectedParam !== null) {
        params.set('hideRejected', hideRejectedParam);
      }
      if (showOnlyMyPicParam !== null) {
        params.set('showOnlyMyPic', showOnlyMyPicParam);
      }
      const queryString = params.toString();
      navigate(`/ats/jobs/${jobId}${queryString ? `?${queryString}` : ''}`);
    } else {
      navigate(-1);
    }
  };

  const handleSubmitNote = () => {
    if (!noteContent.trim()) return;
    createNoteMutation.mutate(noteContent, {
      onSuccess: () => {
        setNoteContent('');
        if (cvId) {
          localStorage.removeItem(`cv-note-draft-${cvId}`);
        }
      },
    });
  };

  const handleDeleteNote = (noteId: string) => {
    deleteNoteMutation.mutate(noteId);
  };

  // Reject handlers
  const handleRejectJob = (jobId: string) => {
    setSelectedJobId(jobId);
    setShowRejectModal(true);
  };

  const handleUnrejectJob = async (jobId: string) => {
    if (!user || !currentWorkspace || !cvId) return;

    setIsProcessing(true);
    try {
      const token = await user.getIdToken();
      await jobService.bulkUpdateCVJobs(jobId, currentWorkspace.id, token, {
        cv_ids: [cvId],
        is_rejected: false,
      });

      // Refetch CV data
      queryClient.invalidateQueries({ queryKey: ['cv', cvId] });
    } catch (err) {
      console.error('Error unrejecting CV:', err);
      alert(err instanceof Error ? err.message : t('cv.detail.errors.unrejectFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmReject = async (reasonId: string, customText: string) => {
    if (!user || !currentWorkspace || !cvId || !selectedJobId) {
      throw new Error(t('cv.detail.errors.missingData'));
    }

    try {
      const token = await user.getIdToken();
      await jobService.bulkUpdateCVJobs(selectedJobId, currentWorkspace.id, token, {
        cv_ids: [cvId],
        is_rejected: true,
        rejection_reason_id: reasonId,
        rejection_reason_text: customText || null,
      });

      // Refetch CV data
      queryClient.invalidateQueries({ queryKey: ['cv', cvId] });

      // Close modal and reset
      setShowRejectModal(false);
      setSelectedJobId(null);
    } catch (err) {
      console.error('Error rejecting CV:', err);
      alert(err instanceof Error ? err.message : t('cv.detail.errors.rejectFailed'));
      throw err; // Re-throw to let modal handle the error state
    }
  };

  const handleAddToJob = async (jobIds: string[]) => {
    if (!user || !currentWorkspace || !cvId) {
      throw new Error(t('cv.detail.errors.missingData'));
    }

    try {
      const token = await user.getIdToken();

      // Add CV to all selected jobs (backend will add to first pipeline round)
      await cvService.addJobsToCV(
        cvId,
        currentWorkspace.id,
        token,
        jobIds
      );

      // Refetch CV data to show the new jobs
      queryClient.invalidateQueries({ queryKey: ['cv', cvId] });
    } catch (err) {
      console.error('Error adding CV to jobs:', err);
      throw err; // Re-throw to let dialog handle the error
    }
  };

  const handleRemoveFromPool = async (poolId: string) => {
    if (!user || !currentWorkspace || !cvId) return;

    try {
      const token = await user.getIdToken();
      await cvService.bulkRemoveCVsFromPools([cvId], currentWorkspace.id, token, [poolId]);
      queryClient.invalidateQueries({ queryKey: ['cv', cvId] });
    } catch (err) {
      console.error('Error removing CV from pool:', err);
      alert(err instanceof Error ? err.message : t('cv.detail.errors.removeFromPoolFailed'));
    }
  };

  const handleSavePoolChanges = async (toAdd: string[], toRemove: string[]) => {
    if (!user || !currentWorkspace || !cvId) {
      throw new Error(t('cv.detail.errors.missingData'));
    }

    const token = await user.getIdToken();
    await cvService.bulkSyncCVPools([cvId], currentWorkspace.id, token, toAdd, toRemove);
    queryClient.invalidateQueries({ queryKey: ['cv', cvId] });
  };

  const handleConfirmDeleteCV = () => {
    if (!cvId) return;
    deleteCVMutation.mutate(cvId, {
      onSuccess: () => {
        // Navigate away after deletion
        if (jobId) {
          navigate(`/ats/jobs/${jobId}`);
        } else {
          navigate('/ats/cvs');
        }
      },
      onError: (err) => {
        alert(err instanceof Error ? err.message : t('cv.detail.errors.deleteFailed'));
        setShowDeleteConfirm(false);
      },
    });
  };

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">{t('cv.detail.title')}</h1>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title={t('cv.detail.close')}
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('cv.detail.loadFailed')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!cv) {
    return null;
  }

  const basics = cv.parsed_data?.basics;
  const displayName = cv.name;
  const displayLabel = basics?.label;
  const hasViewableAttachments = viewableAttachments.length > 0;
  const notes = notesData?.items || [];

  return (
    <div className="space-y-6 relative">
      {/* Merge loading overlay */}
      {isMerging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70 rounded-lg">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p className="text-sm font-medium text-gray-700">{t('cv.detail.merging')}</p>
          </div>
        </div>
      )}
      {/* Header with X button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {basics?.image ? (
            <img
              src={basics.image}
              alt={displayName}
              className="h-16 w-16 rounded-full object-cover border-2 border-gray-200"
            />
          ) : (
            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
            {displayLabel && <p className="text-lg text-gray-600">{displayLabel}</p>}
            <div className="flex items-center gap-3 mt-1">
              <StatusBadge status={cv.processing_status} />
              {jobId && roundId && totalInRound > 0 && (
                <span className="text-sm text-gray-500">
                  {t('cv.detail.indexInRound', { current: currentIndex + 1, total: totalInRound })}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Navigation buttons - only show if we have job context */}
          {jobId && roundId && (
            <div className="flex items-center gap-1 mr-2">
              <button
                onClick={() => previousCvId && handleNavigateCV(previousCvId)}
                disabled={!previousCvId}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title={t('cv.detail.previousCv')}
              >
                <ChevronLeft className="h-6 w-6 text-gray-500" />
              </button>
              <button
                onClick={() => nextCvId && handleNavigateCV(nextCvId)}
                disabled={!nextCvId}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title={t('cv.detail.nextCv')}
              >
                <ChevronRight className="h-6 w-6 text-gray-500" />
              </button>
            </div>
          )}
          {/* Send Email button */}
          <button
            onClick={() => setShowSendEmailDialog(true)}
            className="p-2 hover:bg-orange-50 rounded-full transition-colors text-gray-400 hover:text-orange-600"
            title={t('cv.detail.sendEmail')}
          >
            <Mail className="h-5 w-5" />
          </button>
          {/* Delete button - visible for leaders/above or recruiter PIC */}
          {(isLeaderOrAbove || canReject) && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-2 hover:bg-red-50 rounded-full transition-colors text-gray-400 hover:text-red-600"
              title={t('cv.detail.deleteCv')}
            >
              <Trash2 className="h-5 w-5" />
            </button>
          )}
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title={t('cv.detail.close')}
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Main Content - 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - CV Info with Tabs (2/3) */}
        <div className="lg:col-span-2">
          {hasViewableAttachments ? (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="flex items-center justify-between mb-4">
                <TabsList>
                  <TabsTrigger value="parsed">{t('cv.detail.tabGeneral')}</TabsTrigger>
                  <TabsTrigger value="original">{t('cv.detail.tabResume')}</TabsTrigger>
                </TabsList>

                {/* Attachment Selector - only show if multiple viewable files */}
                {viewableAttachments.length > 1 && activeTab === 'original' && (
                  <select
                    value={currentPdfAttachmentId}
                    onChange={(e) => setSelectedPdfId(e.target.value)}
                    className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white"
                  >
                    {viewableAttachments.map((att) => (
                      <option key={att.id} value={att.id}>
                        {att.file_name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <TabsContent value="parsed" className="mt-0">
                {cv.parsed_data && <CVInfoDisplay data={cv.parsed_data} />}
              </TabsContent>

              <TabsContent value="original" className="mt-0">
                {signedUrlLoading ? (
                  <div className="flex items-center justify-center h-[800px] bg-gray-50 rounded-lg border border-gray-200">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : signedUrlError ? (
                  <div className="flex flex-col items-center justify-center h-[800px] bg-gray-50 rounded-lg border border-gray-200">
                    <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                    <p className="text-gray-700 font-medium mb-2">{t('cv.detail.pdfLoadFailed')}</p>
                    <p className="text-gray-500 text-sm">
                      {signedUrlError instanceof Error ? signedUrlError.message : t('cv.detail.errors.unknown')}
                    </p>
                  </div>
                ) : signedUrlData && signedUrlData.preview_available === false ? (
                  <div className="flex flex-col items-center justify-center h-[800px] bg-gray-50 rounded-lg border border-gray-200">
                    <FileText className="h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-700 font-medium mb-2">{t('cv.detail.pdfNotAvailable')}</p>
                    <p className="text-gray-500 text-sm">{t('cv.detail.pdfClickParseHint')}</p>
                  </div>
                ) : signedUrlData ? (
                  <div className="h-[800px]">
                    <PDFViewer url={signedUrlData.signed_url} fileName={signedUrlData.file_name} />
                  </div>
                ) : null}
              </TabsContent>
            </Tabs>
          ) : (
            // No viewable attachments - just show parsed data without tabs
            cv.parsed_data && <CVInfoDisplay data={cv.parsed_data} />
          )}
        </div>

        {/* Right Column - Recruitment Info, Applied Jobs, Attachments & Notes (1/3) */}
        <div className="space-y-6">
          {/* Duplicate CV Alert */}
          <DuplicateAlert
            cvId={cvId!}
            workspaceId={currentWorkspace?.id}
            idToken={idToken || undefined}
            onMergingChange={setIsMerging}
          />

          <RecruitmentInfo
            cv={cv}
            onAddToPool={() => setShowAddToPoolDialog(true)}
            onRemoveFromPool={handleRemoveFromPool}
          />

          <CVTagsSection tags={cv.tags} />

          {/* Applied Jobs with Pipeline Timeline for each job */}
          <AppliedJobsSection
            cv={cv}
            cvId={cvId!}
            onRejectJob={handleRejectJob}
            onUnrejectJob={handleUnrejectJob}
            onScheduleEvent={(jobId) => {
              setCalendarJobId(jobId);
              setCalendarFormOpen(true);
            }}
            onAddToJob={() => setShowAddToJobDialog(true)}
            isProcessing={isProcessing}
            workspaceId={currentWorkspace?.id}
            idToken={idToken || undefined}
            canReject={canReject}
          />


          <AttachmentsSection
            attachments={cv.attachments}
            cvId={cvId!}
            workspaceId={currentWorkspace?.id}
            idToken={idToken || undefined}
            isProcessing={cv.processing_status === 'processing'}
          />
          {canReject && (
            <NotesSection
              notes={notes}
              isLoading={notesLoading}
              isSubmitting={createNoteMutation.isPending}
              noteContent={noteContent}
              onNoteContentChange={setNoteContent}
              onSubmitNote={handleSubmitNote}
              onDeleteNote={handleDeleteNote}
              deletingNoteId={deleteNoteMutation.isPending ? deleteNoteMutation.variables : null}
              title={t('cv.detail.notesTitle')}
              draftKey={cvId ? `cv-note-draft-${cvId}` : undefined}
            />
          )}
          <CVMeetingsSection
            cvId={cvId!}
            workspaceId={currentWorkspace?.id}
            idToken={idToken || undefined}
          />

          {/* Email History */}
          {cvId && (
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Mail className="h-5 w-5 text-orange-500" />
                {t('cv.detail.emailHistory')}
              </h3>
              <EmailThreadPanel cvId={cvId} />
            </div>
          )}
        </div>
      </div>

      {/* Reject Reason Modal */}
      <RejectReasonModal
        open={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setSelectedJobId(null);
        }}
        onConfirm={handleConfirmReject}
        workspaceId={currentWorkspace?.id}
        idToken={idToken || undefined}
      />

      {/* Add to Job Dialog */}
      <AddCVToJobDialog
        open={showAddToJobDialog}
        onClose={() => setShowAddToJobDialog(false)}
        onConfirm={handleAddToJob}
        workspaceId={currentWorkspace?.id}
        idToken={idToken || undefined}
        cvId={cvId!}
      />

      {/* Calendar Event Form Dialog */}
      <CalendarEventFormDialog
        event={null}
        open={calendarFormOpen}
        onOpenChange={setCalendarFormOpen}
        cvId={cvId!}
        jobId={calendarJobId || undefined}
      />

      {/* Send Email Dialog */}
      {cvId && (
        <SendEmailDialog
          open={showSendEmailDialog}
          onClose={() => setShowSendEmailDialog(false)}
          cvIds={[cvId]}
          jobId={jobId || undefined}
        />
      )}

      {/* Manage Pools Dialog */}
      <ManageCVPoolsDialog
        open={showAddToPoolDialog}
        onClose={() => setShowAddToPoolDialog(false)}
        onSave={handleSavePoolChanges}
        workspaceId={currentWorkspace?.id}
        idToken={idToken || undefined}
        existingPools={cv.pools || []}
      />

      {/* Delete CV Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">{t('cv.detail.deleteCv')}</h2>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              {t('cv.detail.deleteCvConfirmPrefix')}{' '}
              <span className="font-medium text-gray-900">{displayName}</span>
              {t('cv.detail.deleteCvConfirmSuffix')}
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {t('cv.detail.deleteCvWarning')}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteCVMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {t('config.cancel')}
              </button>
              <button
                onClick={handleConfirmDeleteCV}
                disabled={deleteCVMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {deleteCVMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('config.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
